import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Menu } from 'lucide-react'
import { AppFooter } from '@/components/molecules/app-footer'
import { IconButton } from '@/components/ui/icon-button'
import { WorkspacesSidebar } from '@/components/molecules/workspaces/workspaces-sidebar'
import { GtmBalanceBanner } from '@/features/gtm/workspace/billing/gtm-balance-banner'
import { McpAuthorizationDialog } from '@/features/mcp/components/mcp-authorization-dialog'
import { useWorkspaceShellContext } from '@/features/workspaces/domain/use-workspace-shell-context'
import { useWorkspaceShellTheme } from '@/app/theme/use-workspace-shell-theme'
import { cn } from '@/shared/lib/utils'

interface MainLayoutProps {
  children: React.ReactNode
}

export function MainLayout({ children }: MainLayoutProps) {
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false)
  const { t } = useTranslation('common')
  const { isGtmRoute: isGtmWorkspace, workspaceId } = useWorkspaceShellContext()
  useWorkspaceShellTheme()

  return (
    <div className="fixed inset-0 flex flex-col overflow-hidden bg-background text-foreground">
      <IconButton
        icon={<Menu className="h-5 w-5" />}
        variant="ghost"
        className="fixed left-3 top-3 z-30 bg-background/80 backdrop-blur md:hidden"
        ariaLabel={t('openNavigation')}
        onClick={() => setMobileSidebarOpen(true)}
      />
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden md:flex-row">
        <WorkspacesSidebar
          mobileOpen={mobileSidebarOpen}
          onMobileOpenChange={setMobileSidebarOpen}
        />
        <main className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          {isGtmWorkspace && workspaceId ? (
            <GtmBalanceBanner workspaceId={workspaceId} />
          ) : null}
          <div
            className={cn(
              'flex min-h-0 min-w-0 flex-1 flex-col px-4 pt-14 focus:outline-none sm:px-6 md:mx-10 md:px-0 md:pt-0',
              isGtmWorkspace
                ? 'overflow-y-auto overscroll-contain'
                : 'overflow-hidden',
            )}
            tabIndex={isGtmWorkspace ? 0 : undefined}
          >
            {children}
          </div>
        </main>
      </div>
      <AppFooter />
      <McpAuthorizationDialog />
    </div>
  )
}
